import "@arco-design/web-react/es/Card/style";
import _Card from "@arco-design/web-react/es/Card";
import "@arco-design/web-react/es/Form/style";
import _Form from "@arco-design/web-react/es/Form";
import "@arco-design/web-react/es/Skeleton/style";
import _Skeleton from "@arco-design/web-react/es/Skeleton";
import "@arco-design/web-react/es/Space/style";
import _Space from "@arco-design/web-react/es/Space";
import _IconScissor from "@arco-design/web-react/icon/react-icon/IconScissor";
import "@arco-design/web-react/es/Tooltip/style";
import _Tooltip from "@arco-design/web-react/es/Tooltip";
import "@arco-design/web-react/es/Button/style";
import _Button from "@arco-design/web-react/es/Button";
import "@arco-design/web-react/es/Message/style";
import _Message from "@arco-design/web-react/es/Message";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flowExcelStore, setCurrentFieldsMap, setUniqueMapByVariables, useFormEvent } from '@/store/flowExcelStore';
import { useMutationObserver, useRequest } from 'ahooks';
import { useSelector } from 'react-redux';
import { getFormPropertiesByUserTask, getTplContentWithFieldsByCode } from '@/services';
import { flattenToIndexedFormJson, getJsx, getKey } from '@/utils/jsx';
import { RULE_TYPES } from '@/types/ruleType';
import { idKeyToNameKey } from '@/utils/formItem';
import { prefix } from '@/utils/prefix';
import { PdfIcon } from '../Icons';
import { createEmptyTableCropGuide, getTableCropGuideAtPoint, insertTableCropSplitRow } from './excelCropUtils';
import { ExcelTableCropOverlay } from './ExcelTableCropOverlay';

/**
 * Excel 分支的业务表单主体。封装：
 *   - 拉模板（getTplContentWithFieldsByCode）
 *   - 拉审批字段映射（getFormPropertiesByUserTask，仅 add / approve 模式）
 *   - 写回主表单的「当前用户」类字段（姓名 / id 等）
 *   - 编译模板 → jsx / 解析 style 字符串
 *   - 渲染 Card / Form / Skeleton / 动态 jsx
 *
 * 上游 FlowCenterModal 在 kind === 'excel' 时挂载本组件；
 * 其它分支（kind === 'form'）挂 LeaveForm。本组件自身持有"模板加载"状态，
 * 上游无需关心 jsx / style / loading。
 */

function ExcelFormBody({
  form,
  formCode,
  data,
  nodeId,
  mode,
  isView,
  init
}) {
  const userInfo = useSelector(state => state.userInfo);
  const [state, setState] = useState({
    style: '',
    jsx: ''
  });
  const [formEvent] = useFormEvent();

  /**
   * 把业务数据同步写进主表单。view 模式下 ModalContext 是异步拉业务数据后
   * 再 setActiveConfig，所以 ExcelFormBody mount 时 data 可能是空对象，
   * 依赖 `data` 变化触发，等真实数据回来后再写。
   */
  useEffect(() => {
    if (data?.id && form) {
      form.setFieldsValue(flattenToIndexedFormJson(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, form]);

  /**
   * 拉审批字段映射（add / approve 模式才需要）。它的 onSuccess 会写
   * 「申请人/部门经理」之类字段到主表单。
   */
  const getFormPropertiesByUserTaskApi = useRequest(getFormPropertiesByUserTask, {
    manual: true,
    onSuccess: res => {
      const {
        properties
      } = res || {};
      const fieldMap = {};
      setCurrentFieldsMap(properties, field => {
        const key = getKey(field.type, field.variable);
        const item = flowExcelStore.uniqueMap.get(key) || {};
        if ([RULE_TYPES.NAME.value, RULE_TYPES.NI.value].includes(item.type)) {
          fieldMap[`${key}@0`] = userInfo.userId;
          fieldMap[`${idKeyToNameKey(key, 'Name')}@0`] = userInfo.userName;
        }
      });
      // 下一帧写入主表单 —— 模板渲染后字段才注册到 form 上
      setTimeout(() => {
        form.setFieldsValue(fieldMap);
      });
    }
  });

  /**
   * 加载模板 + 编译 jsx。依赖 [formCode, nodeId, mode, data?.id]：
   *   - formCode 变化 → 重新拉模板
   *   - add/approve 模式 → 拉 formProperty
   *   - data.id 变化（如 view 模式异步回来的业务数据）→ 用最新 data 重新编译 jsx，
   *     让 viewMap 把数据"烧"进模板字符串
   *
   * 注意：data 引用每次 render 都是新对象，但只有 id 在业务上有意义。
   * add 模式下 data?.id 始终是 undefined：用 'add' 字面量作占位，避免空跑。
   */
  const dataId = data?.id ?? (mode === 'add' ? '__add__' : null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!formCode) return;
    // data?.id 未到位（如 view 模式异步数据还没回来）→ 暂不跑，等下次 dataId 变化再跑
    if (dataId === null) return;
    const destroyed = {
      current: false
    };
    (async () => {
      // try {
      //   const { content, fields } = await getTplContentWithFieldsByCode(
      //     formCode
      //   );
      //   setUniqueMapByVariables(fields, (field) => {
      //     field.onChange = formEvent?.onChange || (() => {
      //       console.log('onChange', 1);
      //     });
      //   });

      //   if (mode === 'add' || mode === 'approve') {
      //     await getFormPropertiesByUserTaskApi.runAsync(formCode, nodeId);
      //   }

      //   // 已有旧业务数据 → 写入主表单（与 FlowCenterModal 写 form.setFieldsValue 不重复）
      //   if (data?.id && form) {
      //     form.setFieldsValue(flattenToIndexedFormJson(data));
      //   }

      //   const { style, jsx } = getJsx(content, flattenToIndexedFormJson(data));
      //   if (!destroyed.current) {
      //     setState({ style, jsx });
      //   }
      // } catch (e) {
      //   const err = e as Error | unknown;
      //   const msg =
      //     err && typeof err === 'object' && 'message' in err
      //       ? String((err as { message?: string }).message)
      //       : String(err);
      //   Message.error(`BPMN 模板加载失败：${msg}`);
      // }
      const {
        content,
        fields
      } = await getTplContentWithFieldsByCode(formCode);
      setUniqueMapByVariables(fields, field => {
        field.onChange = formEvent?.onChange || (() => {
          console.log('onChange', 1);
        });
      });
      if (mode === 'add' || mode === 'approve') {
        await getFormPropertiesByUserTaskApi.runAsync(formCode, nodeId);
      }

      // 已有旧业务数据 → 写入主表单（与 FlowCenterModal 写 form.setFieldsValue 不重复）
      if (data?.id && form) {
        form.setFieldsValue(flattenToIndexedFormJson(data));
      }
      const {
        style,
        jsx
      } = getJsx(content, flattenToIndexedFormJson(data));
      if (!destroyed.current) {
        setState({
          style,
          jsx
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formCode, nodeId, mode, dataId]);
  const cropContainerRef = useRef(null);
  // 记录已为哪一次 state.jsx 执行过 init，避免重复触发
  const initForRef = useRef(null);

  /**
   * 模板渲染成 table.excel-table 之后才能做 DOM 操作。
   * .excel-table 不一定随 state.jsx 的首次 commit 立即出现（模板内部子组件
   * 可能延迟渲染），因此这里不靠 setTimeout 赌时机，而是：
   *   1) state.jsx 变化后先尝试一次（同步渲染完成的情形）；
   *   2) 用 useMutationObserver 监听容器，等 .excel-table 真正出现再触发（确定性等待）。
   */
  const tryInit = useCallback(() => {
    if (!state.jsx) return;
    if (initForRef.current === state.jsx) return;
    if (!document.querySelector('.excel-table')) return;
    initForRef.current = state.jsx;
    init?.();
  }, [state.jsx, init]);
  useEffect(() => {
    tryInit();
  }, [tryInit]);
  useMutationObserver(() => tryInit(), cropContainerRef, {
    childList: true,
    subtree: true
  });

  // 注意：data 变化时（如审批操作后回传的新 data）—— 当前不在这里重新编译 jsx。
  // 若需要刷新 jsx，让上层 ModalContext 在 data 变化时卸载 ExcelFormBody 后重挂载。

  function onExport() {
    const splitRow = document.querySelectorAll('.crop-split-row');
    const pageIds = Array.from(splitRow).map(row => row.dataset.pageId || '');

    // window.open(`${prefix.tpl}/form/export/${formCode}/${data.id}.pdf`);
    // 1) 浏览器内直接预览（推荐）disposition有attachment(下载文件)、inline(在线预览)
    let url = `${prefix.tpl}/form/export/${formCode}/${data.id}.pdf?disposition=inline`;
    if (pageIds.length) {
      url = `${prefix.tpl}/form/export/${formCode}/${data.id}.pdf?pageIds=${pageIds.join(',')}&disposition=inline`;
    }
    window.open(url, '_blank');
  }

  // 裁剪切换。逻辑参考 umi/sub 项目里的裁剪按钮：
  //  - 只能 view 模式裁剪（不可编辑态下视觉分段）
  //  - 切换时清理/恢复裁剪辅助元素
  //  - 启用后监听容器 mousemove，实时算裁剪线位置
  //  - 点剪刀按钮 → 插入/移除 split 行
  const canCrop = !!isView;
  const canExport = !!isView;
  const [cropEnabled, setCropEnabled] = useState(false);
  const [cropGuide, setCropGuide] = useState(createEmptyTableCropGuide());
  const cropPointerRef = useRef({
    x: 0,
    y: 0
  });
  const hideCropGuide = useCallback(() => {
    setCropGuide(createEmptyTableCropGuide());
  }, []);
  const refreshCropGuide = useCallback((clientX, clientY) => {
    cropPointerRef.current = {
      x: clientX,
      y: clientY
    };
    const nextGuide = getTableCropGuideAtPoint(cropContainerRef.current, clientX, clientY);
    if (!nextGuide.visible) {
      setCropGuide(prev => prev.visible ? createEmptyTableCropGuide() : prev);
      return;
    }
    setCropGuide(nextGuide);
  }, []);
  const onConfirmCrop = useCallback(event => {
    event.stopPropagation();
    insertTableCropSplitRow(cropGuide.table, cropGuide.rowIndex);
  }, [cropGuide.table, cropGuide.rowIndex]);
  function onToggleCrop() {
    if (!canCrop) {
      _Message.warning('当前模式不可裁剪');
      return;
    }
    setCropEnabled(prev => {
      if (prev) hideCropGuide();
      return !prev;
    });
  }
  return /*#__PURE__*/React.createElement(_Card, {
    bordered: false,
    className: "lx-excel-card",
    extra: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Space, {
      direction: "vertical"
    }, canExport && /*#__PURE__*/React.createElement(_Tooltip, {
      position: "left",
      content: "\u5BFC\u51FAPDF"
    }, /*#__PURE__*/React.createElement(_Button, {
      icon: /*#__PURE__*/React.createElement(PdfIcon, null),
      shape: "circle",
      type: "primary",
      onClick: onExport
    })), canCrop && /*#__PURE__*/React.createElement(_Tooltip, {
      position: "left",
      content: "\u88C1\u526A"
    }, /*#__PURE__*/React.createElement(_Button, {
      icon: /*#__PURE__*/React.createElement(_IconScissor, {
        style: {
          fontSize: 16
        }
      }),
      shape: "circle",
      type: cropEnabled ? 'secondary' : 'primary',
      onClick: onToggleCrop
    }))))
  }, /*#__PURE__*/React.createElement(_Form, {
    form: form,
    labelCol: {
      span: 0
    },
    wrapperCol: {
      span: 24
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "lx-table-box"
  }, /*#__PURE__*/React.createElement("div", {
    className: "lx-table-page",
    ref: cropContainerRef,
    onMouseMove: cropEnabled ? e => {
      const target = e.target;
      if (target?.closest?.('[data-crop-overlay-controls="true"]')) {
        return;
      }
      refreshCropGuide(e.clientX, e.clientY);
    } : undefined,
    onMouseLeave: cropEnabled ? () => setCropGuide(createEmptyTableCropGuide()) : undefined,
    style: {
      position: 'relative'
    }
  }, /*#__PURE__*/React.createElement(_Skeleton, {
    animation: true,
    loading: !state.jsx
  }, state.jsx, state.style && /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: state.style
    }
  })), cropEnabled && /*#__PURE__*/React.createElement(ExcelTableCropOverlay, {
    visible: cropGuide.visible,
    top: cropGuide.top,
    left: cropGuide.left,
    width: cropGuide.width,
    onConfirmCrop: onConfirmCrop
  })))));
}
export { ExcelFormBody };